import React from "react";
import { motion } from "framer-motion";

function LegalSection({ number, title, children }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4 }}
      className="group relative w-full overflow-hidden rounded-3xl border border-white/10 bg-white/[0.035] p-6 transition duration-300 hover:border-white/[0.16] hover:bg-white/[0.05] sm:p-8 lg:p-10"
    >
      <div className="pointer-events-none absolute right-0 top-0 h-56 w-56 rounded-full bg-pink-500/[0.025] blur-3xl" />

      <div className="relative flex items-start gap-4 sm:gap-5 lg:gap-6">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.05] text-sm font-black text-pink-300 sm:h-14 sm:w-14 sm:text-base">
          {number}
        </div>

        <div className="min-w-0 flex-1">
          <h2 className="text-xl font-bold text-white sm:text-2xl lg:text-[26px]">
            {title}
          </h2>

          <div className="mt-5 space-y-4 text-sm leading-7 text-slate-300 sm:text-base sm:leading-8 lg:text-[17px] [&_strong]:font-semibold [&_strong]:text-white">
            {children}
          </div>
        </div>
      </div>
    </motion.article>
  );
}

function InfoBox({ title, children }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-black/10 p-5">
      <span className="block text-xs uppercase tracking-wider text-slate-500">
        {title}
      </span>

      <strong className="mt-2 block break-words text-white">
        {children}
      </strong>
    </div>
  );
}

export default function TermsConditions() {
  return (
    <main className="w-full overflow-hidden">

      <section className="relative w-full lg:w-[80%] mx-auto overflow-hidden px-4 py-20 sm:px-6">

        <div className="pointer-events-none absolute left-1/2 top-20 h-[300px] w-[700px] -translate-x-1/2 rounded-full bg-pink-600/10 blur-[140px]" />

        <div className="relative mx-auto mb-14 w-full max-w-6xl text-center">

          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.06] px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-pink-300"
          >
            Legal Information
          </motion.div>


          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="mt-5 text-4xl font-black text-white sm:text-5xl lg:text-6xl"
          >
            OjMee Technology

            <span className="mt-2 block bg-gradient-to-r from-pink-400 via-fuchsia-400 to-violet-400 bg-clip-text text-transparent">
              Terms & Conditions
            </span>
          </motion.h1>


          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="mx-auto mt-6 max-w-5xl text-base leading-8 text-slate-300 sm:text-lg"
          >
            These Terms & Conditions govern your access to and use of
            OjMee Technology websites, SaaS products, cloud-based business
            software, applications, subscription services and related
            digital services.
          </motion.p>


          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.25 }}
            className="mt-7 flex flex-wrap justify-center gap-3"
          >
            <span className="rounded-full border border-white/10 bg-white/[0.04] px-4 py-2 text-xs text-slate-400 sm:text-sm">
              Effective Date: 24 September 2026
            </span>

            <span className="rounded-full border border-white/10 bg-white/[0.04] px-4 py-2 text-xs text-slate-400 sm:text-sm">
              Last Updated: 24 September 2026
            </span>
          </motion.div>

        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative mb-8 w-full rounded-3xl border border-white/10 bg-white/[0.035] p-6 backdrop-blur-sm sm:p-8 lg:p-10"
        >
          <p className="text-sm leading-8 text-slate-300 sm:text-base lg:text-[17px]">
            For the purpose of these Terms,{" "}

            <strong className="text-white">
              “OjMee Technology”, “we”, “us” and “our”
            </strong>{" "}

            refer to OjMee Technology, while{" "}

            <strong className="text-white">
              “User”, “Customer”, “you” and “your”
            </strong>{" "}

            refer to the individual, business or organization accessing
            or using our websites, software products or services.
          </p>

          <p className="mt-4 text-sm leading-8 text-slate-300 sm:text-base lg:text-[17px]">
            By creating an account, purchasing a subscription, accessing
            a product or otherwise using an OjMee Technology service,
            you agree to these Terms & Conditions and any applicable
            product-specific terms displayed to you.
          </p>
        </motion.div>

        <div className="relative w-full space-y-5">

          <LegalSection
            number="01"
            title="About OjMee Technology"
          >
            <p>
              OjMee Technology is a SaaS and business software company
              that develops cloud-based software products, business
              applications and connected digital platforms.
            </p>

            <p>
              Our software may include solutions for invoicing and billing,
              HR and workforce management, customer relationship management,
              ERP and business operations, analytics, remote access,
              automation and other business functions.
            </p>

            <p>
              OjMee Technology may also provide custom software development,
              integrations, APIs, mobile applications, cloud solutions,
              technical support and related technology services.
            </p>

            <p>
              Individual products may have different features,
              subscriptions, usage limits and product-specific conditions.
            </p>
          </LegalSection>


          <LegalSection
            number="02"
            title="Account Registration"
          >
            <p>
              Certain OjMee Technology products and services may require
              a registered account.
            </p>

            <p>
              Users are responsible for providing accurate and current
              information and for maintaining the confidentiality of
              passwords, login credentials and other account-access
              information.
            </p>

            <p>
              You are responsible for activity performed through your
              account unless otherwise required by applicable law.
            </p>

            <p>
              You must notify OjMee Technology promptly if you become
              aware of unauthorized access, suspicious activity or
              another security issue involving your account.
            </p>

            <p>
              We may request reasonable account, identity, transaction
              or business verification where necessary for security,
              fraud prevention, payment verification, regulatory
              compliance or protection of our services.
            </p>
          </LegalSection>


          <LegalSection
            number="03"
            title="OjMee SaaS Products & Software Services"
          >
            <p>
              OjMee Technology provides SaaS products and business
              software designed to support different operational
              requirements.
            </p>

            <p>
              Depending on the product, functionality may include
              employee management, CRM, ERP workflows, invoicing,
              quotations, customer and product records, payment records,
              reports, dashboards, analytics, remote access, automation,
              integrations and other business-management features.
            </p>

            <p>
              Available features may differ according to the product,
              subscription plan, account type, configuration or version
              being used.
            </p>

            <p>
              We may add, modify, improve or discontinue features as
              our products and technology evolve, subject to applicable
              obligations.
            </p>
          </LegalSection>


          <LegalSection
            number="04"
            title="Subscription Plans & Usage Limits"
          >
            <p>
              Certain OjMee Technology products may be available through
              free, trial, paid, custom or other subscription plans.
            </p>

            <p>
              Plans may have different prices, user limits, transaction
              limits, invoice limits, storage limits, modules, features,
              integrations or other usage restrictions.
            </p>

            <p>
              The applicable price, billing period, included features
              and usage limits are those displayed or agreed at the
              time of purchase, upgrade or subscription.
            </p>

            <p>
              Unless specifically stated otherwise, subscription fees
              apply only to the subscription period purchased.
            </p>

            <p>
              Applicable taxes may be charged in addition to displayed
              prices where required.
            </p>
          </LegalSection>


          <LegalSection
            number="05"
            title="Payments"
          >
            <p>
              Payments for OjMee Technology products and services may
              be accepted through UPI, payment gateways, banking channels
              or other payment methods made available by us.
            </p>

            <p>
              A payment will be considered successfully received only
              after confirmation through the applicable payment process.
            </p>

            <p>
              For manually verified transactions, users may be required
              to provide information such as a transaction reference,
              UTR or transaction ID.
            </p>

            <p>
              Submission of a payment reference by itself does not
              establish successful payment. OjMee Technology may verify
              the transaction before activating the applicable product,
              subscription or service.
            </p>

            <p>
              Users must not provide false, fabricated, altered,
              duplicated or previously used payment references.
            </p>
          </LegalSection>


          <LegalSection
            number="06"
            title="Subscription & Service Activation"
          >
            <p>
              Paid subscriptions or services may be activated after
              successful payment confirmation or verification.
            </p>

            <p>
              Activation may be delayed where payment is pending,
              failed, disputed, incomplete, under verification or where
              the supplied transaction information cannot be matched
              with our records.
            </p>

            <p>
              OjMee Technology may request additional transaction or
              account information where reasonably necessary to verify
              a payment or activate a service.
            </p>

            <p>
              Custom software projects, integrations or other
              professional services may have separate delivery,
              milestone or activation conditions agreed for the
              applicable project.
            </p>
          </LegalSection>

          <motion.article
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative w-full overflow-hidden rounded-3xl border border-pink-400/20 bg-gradient-to-br from-pink-500/[0.08] via-fuchsia-500/[0.04] to-violet-500/[0.08] p-6 sm:p-8 lg:p-10"
          >
            <div className="pointer-events-none absolute -right-16 -top-16 h-52 w-52 rounded-full bg-pink-500/10 blur-3xl" />

            <div className="relative">

              <div className="flex items-start gap-4 sm:gap-5 lg:gap-6">

                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-pink-400/20 bg-pink-500/10 font-black text-pink-300 sm:h-14 sm:w-14">
                  07
                </div>

                <div>
                  <span className="inline-flex rounded-full border border-pink-400/20 bg-pink-500/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-pink-300">
                    Important
                  </span>

                  <h2 className="mt-3 text-xl font-bold text-white sm:text-2xl lg:text-[26px]">
                    Refund & Cancellation Policy
                  </h2>
                </div>

              </div>


              <div className="mt-6 space-y-4 text-sm leading-8 text-slate-300 sm:text-base lg:text-[17px]">

                <p>
                  A customer may request cancellation or a refund by
                  contacting OjMee Technology through the support channel
                  displayed on the applicable product or service.
                </p>

                <p>
                  Refund eligibility will depend on the circumstances
                  of the transaction, including duplicate payments,
                  incorrect charges, technical payment errors,
                  non-activation after successful payment or other
                  cases accepted by OjMee Technology.
                </p>

                <div className="rounded-2xl border border-white/10 bg-white/[0.05] p-5 sm:p-6">
                  <p className="font-semibold text-white">
                    Where a refund is approved, OjMee Technology will
                    initiate/process the eligible refund within{" "}

                    <span className="text-pink-300">
                      7 working days of approval.
                    </span>
                  </p>
                </div>

                <p>
                  After a refund is initiated, the time required for the
                  amount to appear in the customer's bank account,
                  UPI-linked account, card or other payment instrument
                  may additionally depend on the bank, payment service
                  provider or payment gateway.
                </p>

                <p>
                  Refunds will ordinarily be made to the original
                  payment method where technically and legally possible.
                </p>

                <p>
                  A refund may be refused where there is evidence of
                  fraud, misuse, false payment information, abuse of
                  the service or another legitimate reason permitted
                  by applicable law.
                </p>

                <p>
                  Cancellation of a subscription does not automatically
                  create a right to a refund for a period already used
                  unless required by applicable law or expressly approved
                  under this Refund Policy.
                </p>

                <p>
                  Nothing in this policy is intended to limit rights
                  that cannot lawfully be excluded.
                </p>

              </div>

            </div>
          </motion.article>


          <LegalSection
            number="08"
            title="Failed or Duplicate Payments"
          >
            <p>
              If money is debited but payment confirmation is not
              received, users should first check the transaction status
              with their bank, payment application or payment provider.
            </p>

            <p>
              For duplicate or incorrectly credited transactions, users
              may contact OjMee Technology with the relevant transaction
              information.
            </p>

            <p>
              Failed-payment reversals may be governed by the applicable
              bank, UPI network, card network, payment gateway or other
              payment-system rules rather than OjMee Technology's
              ordinary refund process.
            </p>
          </LegalSection>


          <LegalSection
            number="09"
            title="Subscription Changes"
          >
            <p>
              Users may upgrade to another available subscription plan
              where that option is supported by the applicable product.
            </p>

            <p>
              Downgrades, renewals, plan expiry, user limits, usage
              limits and feature availability may be handled according
              to the conditions displayed for the applicable plan.
            </p>

            <p>
              Changing or cancelling a subscription does not
              automatically entitle the user to a refund for a period
              already used unless required by applicable law or
              expressly approved under our Refund Policy.
            </p>
          </LegalSection>


          <LegalSection
            number="10"
            title="User Responsibilities & Acceptable Use"
          >
            <p>
              Users are responsible for information, files, records,
              customer data, employee data, transaction details,
              business information and other content they enter,
              upload, generate or manage through an OjMee Technology
              product.
            </p>

            <p>
              Users must ensure that they have the appropriate authority
              or lawful basis to use, upload and process information
              through our software.
            </p>

            <p>
              Users must not use our services to commit fraud,
              impersonate another person or business, evade applicable
              taxes, process unlawful transactions, distribute malicious
              software, interfere with systems, gain unauthorized access
              or engage in activity prohibited by applicable law.
            </p>

            <p>
              Users must not intentionally attempt to bypass security
              controls, usage restrictions, subscription limits or
              access-control mechanisms implemented within our services.
            </p>
          </LegalSection>


          <LegalSection
            number="11"
            title="Billing, GST, Tax & Accounting Disclaimer"
          >
            <p>
              Certain OjMee Technology products, including applicable
              invoicing or billing software, may provide features
              designed to assist with GST calculations, invoices,
              quotations, tax-related reports and business records.
            </p>

            <p>
              These software tools are provided for operational
              convenience and do not constitute legal, accounting,
              taxation or financial advice.
            </p>

            <p>
              Unless expressly identified otherwise, OjMee Technology
              is not the GST Portal, GSTN, a government authority,
              financial institution or a substitute for a qualified
              legal, tax or accounting professional.
            </p>

            <p>
              Users remain responsible for reviewing and verifying
              invoices, GSTIN details, HSN/SAC information, tax rates,
              calculations, reports and other information before filing,
              issuing or submitting records to customers, authorities
              or other parties.
            </p>
          </LegalSection>


          <LegalSection
            number="12"
            title="Custom Software & Professional Services"
          >
            <p>
              OjMee Technology may provide custom software development,
              implementation, customization, API integration, migration,
              deployment, technical support or other professional
              technology services.
            </p>

            <p>
              The scope, deliverables, pricing, timeline, milestones,
              responsibilities and support arrangements for such work
              may be governed by a quotation, proposal, statement of
              work, invoice, written communication or separate agreement.
            </p>

            <p>
              Requirements or functionality outside an agreed project
              scope may require additional development time, pricing
              or a revised agreement.
            </p>
          </LegalSection>


          <LegalSection
            number="13"
            title="Data, Backups & Customer Content"
          >
            <p>
              Users retain ownership of business content and data they
              lawfully submit to our services, subject to the permissions
              reasonably necessary for OjMee Technology to host, process,
              transmit, back up and otherwise handle that information
              for providing the applicable service.
            </p>

            <p>
              Users remain responsible for the accuracy and legality of
              information they submit to our software.
            </p>

            <p>
              Depending on the applicable product or service, users
              should maintain appropriate independent copies of critical
              business records where necessary for their operations,
              legal requirements or business continuity.
            </p>
          </LegalSection>


          <LegalSection
            number="14"
            title="Intellectual Property"
          >
            <p>
              OjMee Technology and its products, software, interfaces,
              source materials, designs, branding, logos, graphics,
              documentation and other proprietary components are
              protected by applicable intellectual-property laws.
            </p>

            <p>
              Except where expressly permitted, users may not copy,
              reproduce, sell, sublicense, redistribute, reverse engineer,
              modify or commercially exploit proprietary parts of our
              software or services.
            </p>

            <p>
              Nothing in these Terms transfers ownership of OjMee
              Technology's intellectual property to the user.
            </p>

            <p>
              Users retain ownership of their lawful business content,
              subject to the permissions necessary for us to operate
              and provide the applicable service.
            </p>
          </LegalSection>


          <LegalSection
            number="15"
            title="Service Availability & Maintenance"
          >
            <p>
              We aim to provide reliable access to our SaaS products,
              software and digital services but do not guarantee
              uninterrupted or error-free availability.
            </p>

            <p>
              A service may temporarily become unavailable because of
              maintenance, upgrades, hosting failures, network problems,
              third-party services, software defects, security incidents
              or circumstances beyond our reasonable control.
            </p>

            <p>
              We may perform maintenance, security updates, infrastructure
              improvements and product upgrades where reasonably
              necessary to operate and improve our services.
            </p>
          </LegalSection>


          <LegalSection
            number="16"
            title="Suspension & Termination"
          >
            <p>
              OjMee Technology may restrict, suspend or terminate access
              where reasonably necessary because of security concerns,
              fraudulent activity, misuse, non-payment, violation of
              these Terms or applicable legal requirements.
            </p>

            <p>
              Where appropriate and reasonably possible, we may provide
              notice or an opportunity to resolve the relevant issue.
            </p>

            <p>
              Users may discontinue use of a service subject to applicable
              subscription, payment, project and contractual obligations.
            </p>

            <p>
              Provisions that by their nature should continue after
              termination may remain applicable after access to a service
              ends.
            </p>
          </LegalSection>


          <LegalSection
            number="17"
            title="Limitation of Liability"
          >
            <p>
              To the maximum extent permitted by applicable law,
              OjMee Technology will not be liable for indirect,
              incidental, special or consequential losses arising
              from use of or inability to use a service.
            </p>

            <p>
              Users are responsible for independently reviewing critical
              business, operational, financial, tax and other information
              generated, calculated or stored through our software.
            </p>

            <p>
              OjMee Technology is not responsible for losses resulting
              from incorrect information entered by a user, unauthorized
              use of user credentials or independent third-party services
              outside our reasonable control.
            </p>

            <p>
              Nothing in these Terms excludes or limits liability that
              cannot legally be excluded or limited under applicable law.
            </p>
          </LegalSection>


          <LegalSection
            number="18"
            title="Third-Party Services & Integrations"
          >
            <p>
              OjMee Technology products may rely on or integrate with
              third-party services including hosting providers,
              databases, email services, payment providers, analytics
              platforms, APIs, communication services, cloud
              infrastructure or other technology providers.
            </p>

            <p>
              Third-party products and services may be governed by their
              own terms, privacy policies, availability commitments and
              operational practices.
            </p>

            <p>
              OjMee Technology is not responsible for independent
              third-party services except to the extent responsibility
              cannot legally be excluded.
            </p>
          </LegalSection>


          <LegalSection
            number="19"
            title="Privacy & Data Protection"
          >
            <p>
              Our collection and handling of personal information is
              described in the OjMee Technology Privacy Policy and any
              additional privacy information applicable to a specific
              product.
            </p>

            <p>
              Users of business software are responsible for ensuring
              that information relating to their customers, employees
              or other individuals is collected and used through our
              services in accordance with applicable requirements.
            </p>

            <p>
              By using our services, users acknowledge that information
              may be processed through the systems and service providers
              reasonably necessary to operate the applicable product.
            </p>
          </LegalSection>


          <LegalSection
            number="20"
            title="Changes to Products, Pricing & Features"
          >
            <p>
              OjMee Technology may update its software, plans, features,
              limits, integrations or pricing as products and business
              requirements evolve.
            </p>

            <p>
              Where applicable, updated pricing or material subscription
              changes may apply from a future billing or renewal period
              rather than retrospectively to a completed purchase,
              unless otherwise agreed or legally permitted.
            </p>

            <p>
              Certain features may be introduced, replaced, modified
              or discontinued as part of product development and
              maintenance.
            </p>
          </LegalSection>


          <LegalSection
            number="21"
            title="Changes to These Terms"
          >
            <p>
              We may revise these Terms to reflect changes to our SaaS
              products, business software, security practices, legal
              requirements, payment processes or business operations.
            </p>

            <p>
              The updated version will display a revised{" "}
              <strong>“Last Updated”</strong> date.
            </p>

            <p>
              Where appropriate or legally required, material changes
              may also be communicated through our website, application,
              account interface, email or another suitable channel.
            </p>

            <p>
              Continued use of a service after updated Terms become
              effective may constitute acceptance of those Terms where
              permitted by applicable law.
            </p>
          </LegalSection>


          <LegalSection
            number="22"
            title="Governing Law"
          >
            <p>
              These Terms & Conditions are governed by the applicable
              laws of India.
            </p>

            <p>
              Any dispute will be handled in accordance with applicable
              Indian law and the jurisdiction stated in OjMee
              Technology's official business or legal information,
              subject to mandatory consumer rights and statutory
              dispute-resolution mechanisms that may apply.
            </p>

            <p>
              Nothing in these Terms is intended to remove rights or
              remedies that cannot lawfully be excluded.
            </p>
          </LegalSection>

          <motion.article
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative w-full overflow-hidden rounded-3xl border border-white/10 bg-white/[0.04] p-6 sm:p-8 lg:p-10"
          >
            <div className="pointer-events-none absolute bottom-0 right-0 h-52 w-52 rounded-full bg-violet-500/[0.04] blur-3xl" />


            <div className="relative">

              <div className="flex items-start gap-4 sm:gap-5 lg:gap-6">

                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-violet-400/20 bg-violet-500/10 font-black text-violet-300 sm:h-14 sm:w-14">
                  23
                </div>


                <div>

                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-violet-300">
                    Support & Legal
                  </p>


                  <h2 className="mt-2 text-xl font-bold text-white sm:text-2xl lg:text-[26px]">
                    Contact OjMee Technology
                  </h2>


                  <p className="mt-2 leading-7 text-slate-400">
                    For account, subscription, payment, refund, product,
                    service or legal queries, please contact OjMee
                    Technology using the details below.
                  </p>

                </div>

              </div>


              <div className="mt-7 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">

                <InfoBox title="Company">
                  OjMee Technology
                </InfoBox>


                <InfoBox title="Website">
                  <a
                    href="https://ojmee.in"
                    target="_blank"
                    rel="noreferrer"
                    className="text-pink-300 transition hover:text-pink-200"
                  >
                    www.ojmee.in
                  </a>
                </InfoBox>


                <InfoBox title="Head Office">
                  C-58, Shahpuri Tower, Janakpuri, Delhi - 110058
                </InfoBox>


                <InfoBox title="Email">
                  <a
                    href="mailto:ojmeetech@gmail.com"
                    className="text-pink-300 transition hover:text-pink-200"
                  >
                    ojmeetech@gmail.com
                  </a>
                </InfoBox>

              </div>


              <div className="mt-5 rounded-2xl border border-white/10 bg-black/10 p-5 sm:p-6">
                <p className="text-sm leading-7 text-slate-400 sm:text-base">
                  These Terms apply to OjMee Technology products and
                  services that reference them. A particular SaaS product,
                  subscription or custom software project may also include
                  additional product-specific or project-specific terms.
                </p>
              </div>

            </div>

          </motion.article>

        </div>

      </section>

    </main>
  );
}